import {
  Id,
  Identifiable,
  Project,
  resource as resourceMock,
  list as listMock,
  task as taskMock,
  TimeSheet,
  TimeSheetFilter,
  Resource,
  project as projectMock,
  TimeRecord,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type ResponseList = {
  uuid: Id;
  name: string;
  nbrComments: number;
  spentTimes: { [key: string]: number };
  nbrAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentComplete: number;
  subActivities: ResponseList[];
  tasks: {
    uuid: Id;
    name: string;
    spentTimes: { [key: string]: number };
    nbrAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    timeRecords: {
      uuid: Id;
      date: number;
      month: number;
      year: number;
      timeSpent: number;
      attributes?: { [key: string]: string };
      timeSpentInWeek: number;
    }[];
    taskResourceLatestRecord: string;
    taskResourceLatestRecordingDay: string;
    resourcePercentComplete: number;
    allocationData: {
      allocationUuid: string;
      resourceUuid: string;
    }[];
  }[];
};

type Response = {
  spentTimes: { [key: string]: number };
  resources: {
    uuid: Id;
    name: string;
    profession: string;
    spentTimes: { [key: string]: number };
    unavailabilities: { [key: string]: boolean };
    totalOfAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    percentCompletion: number;
    activityRoot: ResponseList;
  }[];
};

function isUnAvailable(
  at: string,
  unAvailabilities: { [key: string]: boolean },
): boolean {
  return unAvailabilities[at];
}

function responseToSpentTimes(
  res: { [key: string]: number },
  unAvailabilities: { [key: string]: boolean },
): { date: string; time: number; isAvailable: boolean }[] {
  return Object.entries(res)
    .map(([date, time]) => ({
      date,
      time,
      isAvailable: !isUnAvailable(date, unAvailabilities),
    }))
    .sort((first, second) =>
      new Date(first.date) < new Date(second.date) ? -1 : 1,
    );
}

function formatList(
  list: ResponseList,
  project: Identifiable<Project>,
  resource: Identifiable<Resource> & {
    unavailabilities: { [key: string]: boolean };
  },
): TimeSheet["resources"][number]["lists"][number] {
  return {
    ...listMock,
    ...list,
    percentComplete: list.percentComplete,
    project: { ...projectMock, ...project },
    subLists: list.subActivities.map((subList) =>
      formatList(subList, project, resource),
    ),
    spentTimes: responseToSpentTimes(
      list.spentTimes,
      resource.unavailabilities,
    ),
    tasks: list.tasks.map((task) => ({
      ...taskMock,
      ...task,
      allocations: task.allocationData.map((allocation) => ({
        uuid: allocation.allocationUuid,
        allocatedHours: task.nbrAllocatedHours,
        percentComplete: task.resourcePercentComplete,
        estimatedAt: 0,
        completedAt: "",
        resource: { ...resource, uuid: allocation.resourceUuid },
      })),
      spentTimes: responseToSpentTimes(
        task.spentTimes,
        resource.unavailabilities,
      ),
      timeRecords: task.timeRecords.map((record) => ({
        ...record,
        task: { ...taskMock, ...task },
        resource,
      })),
    })),
  };
}

function responseToTimeSheet(
  res: Response,
  project: Identifiable<Project>,
): TimeSheet {
  return {
    spentTimes: responseToSpentTimes(res.spentTimes, {}),
    resources: res.resources.map((resource) => ({
      ...resourceMock,
      ...resource,
      spentTimes: responseToSpentTimes(
        resource.spentTimes,
        resource.unavailabilities,
      ),
      lists: [resource.activityRoot].map((list) =>
        formatList(list, project, { ...resourceMock, ...resource }),
      ),
    })),
  };
}

function reloadProjectTimeSheet(httpClient: HttpClient) {
  return (
    project: Identifiable<Project>,
    record: TimeRecord,
    filter: TimeSheetFilter,
  ) => {
    const min = new Date(filter.minDate);
    const max = new Date(filter.maxDate);

    return httpClient
      .sendHttpRequest<Response>({
        endpoint: `/projects/${project.uuid}/resource/${record.resource.uuid}/timesheets/by-week`,
        method: HttpMethods.GET,
        headers: {
          startDate: min.toISOString(),
          endDate: max.toISOString(),
        },
      })
      .then((timesheet) => responseToTimeSheet(timesheet, project));
  };
}

export { reloadProjectTimeSheet };
